/**
 * RBAC types (UX layer). See doc.md for the full model.
 *
 * Naming conventions:
 *   - Roles    — PascalCase, exactly matching Keycloak role names.
 *   - Features — camelCase entity names.
 *   - Actions  — only `view` | `manage`.
 */

/**
 * The three roles that exist in the system. Values MUST match the role names
 * issued by the `Backoffice` realm — no case drift.
 *
 * - CreditOfficer    — full access to everything.
 * - FinancialOfficer — loan applications + transactions module.
 * - SalesOfficer     — loan applications only (no transactions module).
 */
export const UserRoles = {
    CreditOfficer: 'CreditOfficer',
    FinancialOfficer: 'FinancialOfficer',
    SalesOfficer: 'SalesOfficer',
} as const;

export type UserRole = (typeof UserRoles)[keyof typeof UserRoles];

/**
 * Wildcard rule: the feature is usable by ANY authenticated user, regardless of
 * role. Put it in a feature's action list (`['*']`) instead of enumerating
 * roles — used for role-agnostic features (e.g. agentLoanApplications).
 */
export const ANY_ROLE = '*';

/** A matrix entry is either a concrete role or the `*` wildcard. */
export type RoleRule = UserRole | typeof ANY_ROLE;

export type Action = 'view' | 'manage';

/**
 * Features gated by RBAC in this portal. camelCase entity names, aligned with
 * routes / entity links.
 */
export type AppFeature =
    | 'partners'
    | 'merchants'
    | 'loanApplications'
    | 'agentLoanApplications'
    | 'transactions';

/** Roles (or `*`) allowed to perform each action on a feature. */
export type EntityConfig = Partial<Record<Action, RoleRule[]>>;

/** The permission matrix: feature → per-action allowed roles. */
export type UserPermissionsConfig = Record<AppFeature, EntityConfig>;

/** Nested boolean map exposed as UserStore.can / UserStore.canNot. */
export type ReadablePermissions = Record<Action, Record<AppFeature, boolean>>;
